import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { gpsAPI, systemConfigAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useApi } from '../hooks/useApi';
import { formatDateTime, formatSpeed } from '../utils/helpers';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import Table from '../components/Table';
import {
  MapPinIcon,
  TruckIcon,
  SignalIcon,
  ClockIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PlusIcon,
  ArrowPathIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';

const TruckTrackingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();

  // Check if tracking module is enabled
  const { data: trackingConfig, loading: configLoading } = useApi(
    () => systemConfigAPI.getByKey('module.tracking.enabled'),
    [],
    { showErrorToast: false, immediate: true }
  );

  const isTrackingEnabled = trackingConfig?.value === true || trackingConfig?.value === 'true';

  // Helper function to open Google Maps in modal
  const openGoogleMaps = (latitude, longitude, truckPlateNumber) => {
    setMapLocation({ latitude, longitude, plateNumber: truckPlateNumber });
    setShowMapModal(true);
    toast.info(t('tracking.messages.openingGoogleMaps', { plateNumber: truckPlateNumber }));
  };
  
  const [trucks, setTrucks] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [returningTrucks, setReturningTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showTripModal, setShowTripModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapLocation, setMapLocation] = useState({ latitude: null, longitude: null, plateNumber: '' });
  const [loading, setLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState('returning'); // 'all', 'returning', 'active'

  // Fetch latest truck locations
  const fetchTruckLocations = useCallback(async () => {
    try {
      const params = {};
      if (user.role === 'CLIENT') {
        params.clientId = user.clientId;
      }
      
      const response = await gpsAPI.getLatestLocations(params);
      setTrucks(response.data.trucks || []);
    } catch (error) {
      console.error('Error fetching truck locations:', error);
      if (!trucks.length) {
        toast.error(t('tracking.messages.failedToLoadLocations'));
      }
    }
  }, [user, trucks.length, toast, t]);

  // Fetch active trips
  const fetchActiveTrips = useCallback(async () => {
    try {
      const params = { status: 'STARTED,IN_PROGRESS,DELAYED' };
      if (user.role === 'CLIENT') {
        params.clientId = user.clientId;
      }
      
      const response = await gpsAPI.getTrips(params);
      setActiveTrips(response.data.trips || []);
    } catch (error) {
      console.error('Error fetching active trips:', error);
    }
  }, [user]);

  // Fetch trucks returning from port with LOADED status
  const fetchReturningTrucks = useCallback(async () => {
    try {
      const params = {};
      if (user.role === 'CLIENT') {
        params.clientId = user.clientId;
      }
      
      const response = await gpsAPI.getReturningTrucks(params);
      setReturningTrucks(response.data.returningTrips || []);
    } catch (error) {
      console.error('Error fetching returning trucks:', error);
    }
  }, [user]);

  // Initial data load - only if module is enabled
  useEffect(() => {
    // Don't load data if config is still loading or module is disabled
    if (configLoading || !isTrackingEnabled) {
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTruckLocations(), fetchActiveTrips(), fetchReturningTrucks()]);
      setLoading(false);
    };
    
    loadData();
  }, [configLoading, isTrackingEnabled, fetchTruckLocations, fetchActiveTrips, fetchReturningTrucks]);

  // Auto-refresh setup - only if module is enabled
  useEffect(() => {
    // Don't set up auto-refresh if config is still loading or module is disabled
    if (configLoading || !isTrackingEnabled) {
      return;
    }
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchTruckLocations();
        fetchActiveTrips();
        fetchReturningTrucks();
      }, 30000); // Refresh every 30 seconds
      
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [configLoading, isTrackingEnabled, autoRefresh, fetchTruckLocations, fetchActiveTrips, fetchReturningTrucks, refreshInterval]);

  // Manual refresh
  const handleRefresh = () => {
    fetchTruckLocations();
    fetchActiveTrips();
    fetchReturningTrucks();
  };

  // Get status badge color
  const getStatusColor = (status, isOnline) => {
    if (!isOnline) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'PARKED': return 'bg-blue-100 text-blue-800';
      case 'IN_TRANSIT': return 'bg-green-100 text-green-800';
      case 'AT_PORT': return 'bg-purple-100 text-purple-800';
      case 'AT_DEPOT': return 'bg-yellow-100 text-yellow-800';
      case 'LOADING': return 'bg-orange-100 text-orange-800';
      case 'UNLOADING': return 'bg-orange-100 text-orange-800';
      case 'EMERGENCY': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format last seen time
  const formatLastSeen = (timestamp) => {
    if (!timestamp) return t('tracking.timeAgo.never');
    
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return t('tracking.timeAgo.justNow');
    if (diffMins < 60) return t('tracking.timeAgo.minutesAgo', { minutes: diffMins });
    if (diffMins < 1440) return t('tracking.timeAgo.hoursAgo', { hours: Math.floor(diffMins / 60) });
    return formatDateTime(timestamp);
  };

  // Returning trucks table columns (specific for LOADED status trucks)
  const returningTrucksColumns = [
    {
      key: 'truck',
      title: t('tracking.columns.truckAndDriver'),
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <TruckIcon className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{row.truck.plateNumber}</div>
            <div className="text-sm text-gray-500">{row.truck.driverName}</div>
            {row.truck.driverPhone && (
              <div className="text-xs text-gray-400">{row.truck.driverPhone}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'load',
      title: t('tracking.columns.loadInformation'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">
            {row.load?.product?.name || t('tracking.unknownValues.unknownProduct')}
          </div>
                      <div className="text-sm text-gray-500">
            {t('dashboard.weight')}: {row.load?.netWeight ? `${(row.load.netWeight / 1000).toFixed(2)} Ton` : 'N/A'}
          </div>
          {row.load?.ticket?.ticketNumber && (
            <div className="text-xs text-blue-600">
              {t('loads.ticketNumber')}: {row.load.ticket.ticketNumber}
            </div>
          )}
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.isReturningWithLoad ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
          }`}>
            {row.load?.status || t('tracking.unknownValues.unknownStatus')}
          </span>
        </div>
      ),
    },
    {
      key: 'route',
      title: t('tracking.columns.returnRoute'),
      render: (value, row) => (
        <div className="text-sm">
          <div className="text-gray-900 font-medium">
            📍 {row.originPort?.name || t('tracking.unknownValues.port')}
          </div>
          <div className="text-gray-400 text-center">↓</div>
          <div className="text-blue-600">
            🏭 {row.destinationDepot?.name || t('tracking.unknownValues.depot')}
          </div>
        </div>
      ),
    },
    {
      key: 'currentLocation',
      title: t('tracking.columns.currentLocation'),
      render: (value, row) => {
        const location = row.gpsLocations?.[0];
        if (!location) {
          return (
            <div className="text-red-500 text-sm">
              <div>{t('tracking.location.noGpsSignal')}</div>
              <div className="text-xs">{t('tracking.location.lastSeen')}: {row.lastLocationAge ? t('tracking.timeAgo.minutesAgo', { minutes: row.lastLocationAge }) : t('tracking.timeAgo.never')}</div>
            </div>
          );
        }
        
        return (
          <div className="text-sm">
            <button
              onClick={() => openGoogleMaps(location.latitude, location.longitude, row.truck.plateNumber)}
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              title={t('tracking.tooltips.viewOnGoogleMaps')}
            >
              📍 {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </button>
            <div className="text-gray-500">
              {formatLastSeen(location.timestamp)}
              {location.speed && ` • ${formatSpeed(location.speed)}`}
            </div>
            <div className="flex items-center space-x-1 mt-1">
              {location.isOnline ? (
                <SignalIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
              )}
              <span className={`text-xs ${location.isOnline ? 'text-green-600' : 'text-red-600'}`}>
                {location.isOnline ? t('tracking.status.online') : t('tracking.status.offline')}
              </span>
              {location.batteryLevel && (
                <span className="text-xs text-gray-500">
                  🔋 {location.batteryLevel.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'tripStatus',
      title: t('tracking.columns.tripStatus'),
      render: (value, row) => (
        <div>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'STARTED' ? 'bg-green-100 text-green-800' :
            row.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
            row.status === 'DELAYED' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {t(`tracking.tripStatuses.${row.status?.toLowerCase()}`) || row.status}
          </span>
          <div className="text-xs text-gray-500 mt-1">
            Started: {formatDateTime(row.startTime)}
          </div>
          {row.estimatedArrival && (
            <div className="text-xs text-blue-600">
              ETA: {formatDateTime(row.estimatedArrival)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: t('tracking.columns.actions'),
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleViewHistory(row.truck)}
            title={t('tracking.tooltips.viewGpsHistory')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleUpdateTripStatus(row)}
            title={t('tracking.tooltips.updateTripStatus')}
            className="text-xs"
          >
            {t('tracking.buttons.updateStatus')}
          </Button>
          {row.gpsLocations?.[0] && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openGoogleMaps(row.gpsLocations[0].latitude, row.gpsLocations[0].longitude, row.truck.plateNumber)}
              title={t('tracking.tooltips.viewOnGoogleMaps')}
              className="text-green-600 hover:text-green-800"
            >
              🗺️
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Truck tracking table columns
  const truckColumns = [
    {
      key: 'truck',
      title: t('tracking.columns.truck'),
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <TruckIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{row.truck.plateNumber}</div>
            <div className="text-sm text-gray-500">{row.truck.driverName}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      title: t('status'),
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(row.status, row.isOnline)}`}>
            {row.isOnline ? t(`tracking.status.${row.status?.toLowerCase()}`) || row.status : t('tracking.status.offline')}
          </span>
          {row.isOnline && (
            <SignalIcon className="h-4 w-4 text-green-500" />
          )}
        </div>
      ),
    },
    {
      key: 'location',
      title: t('tracking.columns.location'),
      render: (value, row) => {
        if (!row.location) return <span className="text-gray-500">{t('tracking.location.noGpsSignal')}</span>;
        
        return (
          <div>
            <button
              onClick={() => openGoogleMaps(row.location.latitude, row.location.longitude, row.truck.plateNumber)}
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left text-sm"
              title={t('tracking.tooltips.viewOnGoogleMaps')}
            >
              📍 {row.location.latitude.toFixed(6)}, {row.location.longitude.toFixed(6)}
            </button>
            {row.location.port && (
              <div className="text-xs text-blue-600">{t('tracking.unknownValues.port')}: {row.location.port.name}</div>
            )}
            {row.location.depot && (
              <div className="text-xs text-yellow-600">{t('tracking.unknownValues.depot')}: {row.location.depot.name}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'trip',
      title: t('tracking.columns.currentTrip'),
      render: (value, row) => {
        if (!row.location?.trip) return <span className="text-gray-500">{t('tracking.emptyStates.noActiveTrips')}</span>;
        
        const trip = row.location.trip;
        return (
          <div>
            <div className="text-sm text-gray-900">{trip.tripType.replace('_', ' ')}</div>
            <div className="text-xs text-gray-500">
              {trip.originPort?.name || trip.originDepot?.name} → {trip.destinationPort?.name || trip.destinationDepot?.name}
            </div>
            <span className={`inline-flex px-1 py-0.5 text-xs rounded ${
              trip.status === 'STARTED' ? 'bg-green-100 text-green-800' :
              trip.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
              trip.status === 'DELAYED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {t(`tracking.tripStatuses.${trip.status?.toLowerCase()}`) || trip.status}
            </span>
          </div>
        );
      },
    },
    {
      key: 'lastSeen',
      title: t('tracking.columns.lastSeen'),
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          <div>{formatLastSeen(row.lastSeen)}</div>
          {row.location?.speed && (
            <div className="text-xs text-gray-500">{formatSpeed(row.location.speed)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: t('tracking.columns.actions'),
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleViewHistory(row.truck)}
            title={t('tracking.tooltips.viewHistory')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleCreateTrip(row.truck)}
            title={t('tracking.tooltips.createTrip')}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
          {row.location && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openGoogleMaps(row.location.latitude, row.location.longitude, row.truck.plateNumber)}
              title={t('tracking.tooltips.viewOnGoogleMaps')}
              className="text-green-600 hover:text-green-800"
            >
              🗺️
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Active trips table columns
  const tripColumns = [
    {
      key: 'truck',
      title: t('tracking.columns.truck'),
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.truck.plateNumber}</div>
          <div className="text-sm text-gray-500">{row.truck.driverName}</div>
        </div>
      ),
    },
    {
      key: 'tripType',
      title: t('tracking.columns.tripType'),
      render: (value) => (
        <span className="text-sm text-gray-900">{value.replace('_', ' ')}</span>
      ),
    },
    {
      key: 'route',
      title: t('tracking.columns.route'),
      render: (value, row) => (
        <div className="text-sm">
          <div className="text-gray-900">
            {row.originPort?.name || row.originDepot?.name || 'Unknown'}
          </div>
          <div className="text-gray-500">↓</div>
          <div className="text-gray-900">
            {row.destinationPort?.name || row.destinationDepot?.name || 'Unknown'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'STARTED' ? 'bg-green-100 text-green-800' :
          value === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
          value === 'DELAYED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      ),
    },
    {
      key: 'timing',
      title: t('tracking.columns.timing'),
      render: (value, row) => (
                  <div className="text-sm">
            <div className="text-gray-900">{t('tracking.columns.started')}: {formatDateTime(row.startTime)}</div>
            {row.estimatedArrival && (
              <div className="text-gray-500">{t('tracking.columns.eta')}: {formatDateTime(row.estimatedArrival)}</div>
            )}
        </div>
      ),
    },
    {
      key: 'currentLocation',
      title: t('tracking.columns.currentLocation'),
      render: (value, row) => {
        const location = row.gpsLocations?.[0];
        if (!location) return <span className="text-gray-500">{t('tracking.location.noGpsSignal')}</span>;
        
        return (
          <div className="text-sm">
            <button
              onClick={() => openGoogleMaps(location.latitude, location.longitude, row.truck.plateNumber)}
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              title={t('tracking.tooltips.viewOnGoogleMaps')}
            >
              📍 {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </button>
            <div className="text-gray-500">
              {formatLastSeen(location.timestamp)}
              {location.speed && ` • ${formatSpeed(location.speed)}`}
            </div>
          </div>
        );
      },
    },
  ];

  const handleViewHistory = (truck) => {
    setSelectedTruck(truck);
    setShowHistoryModal(true);
  };

  const handleCreateTrip = (truck) => {
    setSelectedTruck(truck);
    setShowTripModal(true);
  };

  const handleUpdateTripStatus = async (trip) => {
    // Simple status update - in a real app, this could be a modal
    const statusOptions = ['STARTED', 'IN_PROGRESS', 'DELAYED', 'ARRIVED', 'COMPLETED'];
    const currentIndex = statusOptions.indexOf(trip.status);
    const nextStatus = statusOptions[currentIndex + 1];
    
    if (nextStatus) {
      try {
        await gpsAPI.updateTripStatus(trip.id, { status: nextStatus });
        toast.success(`Trip status updated to ${nextStatus}`);
        fetchReturningTrucks();
      } catch (error) {
        console.error('Error updating trip status:', error);
        toast.error(t('tracking.messages.failedToUpdateTripStatus'));
      }
    } else {
              toast.info(t('tracking.messages.tripAlreadyAtFinalStatus'));
    }
  };

  // Show loading while checking module status
  if (configLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show disabled message if tracking module is not enabled
  // Check this early, even if config is still loading (to avoid showing loading state unnecessarily)
  if (!configLoading && !isTrackingEnabled) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <ExclamationTriangleIcon className="h-20 w-20 text-yellow-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tracking.moduleDisabled')}</h2>
          <p className="text-lg text-gray-600 mb-6">{t('tracking.moduleDisabledDescription')}</p>
          {user?.role === 'ADMIN' && (
            <a
              href="/system-config"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              {t('tracking.goToConfig')}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MapPinIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('tracking.title')}</h1>
            <p className="text-gray-600">{t('tracking.subtitle')}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('returning')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                viewMode === 'returning' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🚛 {t('tracking.summary.returningLoaded')}
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                viewMode === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📍 {t('tracking.summary.allTrucks')}
            </button>
            <button
              onClick={() => setViewMode('active')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                viewMode === 'active' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🛣️ {t('tracking.summary.activeTrips')}
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-green-50 text-green-700' : ''}
            >
              {autoRefresh ? <PauseIcon className="h-4 w-4 mr-2" /> : <PlayIcon className="h-4 w-4 mr-2" />}
              {t('tracking.autoRefresh')}
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {t('tracking.refresh')}
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {viewMode === 'returning' ? (
          <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TruckIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.summary.returningTrucks')}</dt>
                      <dd className="text-lg font-medium text-gray-900">{returningTrucks.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ArchiveBoxIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.summary.withLoadedStatus')}</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {returningTrucks.filter(t => t.isReturningWithLoad).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SignalIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.status.online')}</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {returningTrucks.filter(t => t.gpsLocations?.[0]?.isOnline).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.status.offline')}</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {returningTrucks.filter(t => !t.gpsLocations?.[0]?.isOnline).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TruckIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.summary.allTrucks')}</dt>
                      <dd className="text-lg font-medium text-gray-900">{trucks.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SignalIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.status.online')}</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {trucks.filter(t => t.isOnline).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.summary.activeTrips')}</dt>
                      <dd className="text-lg font-medium text-gray-900">{activeTrips.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t('tracking.status.offline')}</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {trucks.filter(t => !t.isOnline).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Conditional Table Rendering Based on View Mode */}
      {viewMode === 'returning' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                🚛 {t('tracking.summary.returningLoadedTitle')}
              </h3>
              <div className="text-sm text-gray-500">
                {returningTrucks.length} trucks • {returningTrucks.filter(t => t.isReturningWithLoad).length} with loaded cargo
              </div>
            </div>
            <Table
              columns={returningTrucksColumns}
              data={returningTrucks}
              loading={false}
              emptyMessage={t('tracking.emptyStates.noReturningTrucks')}
            />
            {returningTrucks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <TruckIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Returning Trucks</h3>
                <p>When trucks complete loading at the port, they will appear here for tracking during their return journey.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'all' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              📍 All Truck Locations
            </h3>
            <Table
              columns={truckColumns}
              data={trucks}
              loading={false}
              emptyMessage={t('tracking.emptyStates.noTrucksFound')}
            />
          </div>
        </div>
      )}

      {viewMode === 'active' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              🛣️ {t('tracking.summary.activeTrips')}
            </h3>
            <Table
              columns={tripColumns}
              data={activeTrips}
              loading={false}
              emptyMessage={t('tracking.emptyStates.noActiveTrips')}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <TruckHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        truck={selectedTruck}
        openGoogleMaps={openGoogleMaps}
      />

      <CreateTripModal
        isOpen={showTripModal}
        onClose={() => setShowTripModal(false)}
        truck={selectedTruck}
        onSuccess={() => {
          fetchActiveTrips();
          setShowTripModal(false);
        }}
      />

      <GoogleMapsModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        location={mapLocation}
      />
    </div>
  );
};

// Truck History Modal Component
const TruckHistoryModal = ({ isOpen, onClose, truck, openGoogleMaps }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    endDate: new Date().toISOString().split('T')[0]
  });

  // Get status badge color function (same as in main component)
  const getStatusColor = (status, isOnline) => {
    if (!isOnline) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'PARKED': return 'bg-blue-100 text-blue-800';
      case 'IN_TRANSIT': return 'bg-green-100 text-green-800';
      case 'AT_PORT': return 'bg-purple-100 text-purple-800';
      case 'AT_DEPOT': return 'bg-yellow-100 text-yellow-800';
      case 'LOADING': return 'bg-orange-100 text-orange-800';
      case 'UNLOADING': return 'bg-orange-100 text-orange-800';
      case 'EMERGENCY': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchHistory = useCallback(async () => {
    if (!truck) return;
    
    try {
      setLoading(true);
      const response = await gpsAPI.getTruckHistory(truck.id, dateRange);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching truck history:', error);
    } finally {
      setLoading(false);
    }
  }, [truck, dateRange]);

  useEffect(() => {
    if (isOpen && truck) {
      fetchHistory();
    }
  }, [isOpen, truck, fetchHistory]);

  if (!truck) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Location History - ${truck.plateNumber}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Date Range Filter */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('tracking.modal.gpsHistory.startDate')}
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
          />
          <Input
            label={t('tracking.modal.gpsHistory.endDate')}
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
          />
        </div>

        {/* History List */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No location history found</div>
          ) : (
            <div className="space-y-2">
              {history.map((location, index) => (
                <div key={location.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <button
                        onClick={() => openGoogleMaps(location.latitude, location.longitude, truck.plateNumber)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left text-sm font-medium"
                        title={t('tracking.tooltips.viewOnGoogleMaps')}
                      >
                        📍 {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                      </button>
                      <div className="text-xs text-gray-500">
                        {formatDateTime(location.timestamp)}
                      </div>
                      {location.speed && (
                        <div className="text-xs text-gray-500">
                          Speed: {formatSpeed(location.speed)}
                        </div>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(location.status, location.isOnline)}`}>
                      {location.status}
                    </span>
                  </div>
                  {location.driverNotes && (
                    <div className="mt-2 text-sm text-gray-600">
                      Note: {location.driverNotes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// Create Trip Modal Component
const CreateTripModal = ({ isOpen, onClose, truck, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tripType: 'TO_PORT',
    originPortId: '',
    destinationPortId: '',
    originDepotId: '',
    destinationDepotId: '',
    estimatedArrival: '',
    operatorNotes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!truck) return;
    
    try {
      setLoading(true);
      await gpsAPI.createTrip({
        truckId: truck.id,
        ...formData,
        estimatedArrival: formData.estimatedArrival || null
      });
      
      toast.success(t('tracking.messages.tripCreatedSuccessfully'));
      onSuccess();
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error(error.response?.data?.message || t('tracking.messages.failedToCreateTrip'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!truck) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Create Trip - ${truck.plateNumber}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Trip Type
          </label>
          <select
            name="tripType"
            value={formData.tripType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="TO_PORT">To Port</option>
            <option value="FROM_PORT">From Port</option>
            <option value="DEPOT_TO_PORT">Depot to Port</option>
            <option value="PORT_TO_DEPOT">Port to Depot</option>
            <option value="INTER_DEPOT">Between Depots</option>
            <option value="RETURN_TRIP">Return Trip</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('tracking.modal.createTrip.estimatedArrival')}
            name="estimatedArrival"
            type="datetime-local"
            value={formData.estimatedArrival}
            onChange={handleChange}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="operatorNotes"
            value={formData.operatorNotes}
            onChange={handleChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('tracking.modal.createTrip.notesPlaceholder')}
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Create Trip
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Google Maps Modal Component
const GoogleMapsModal = ({ isOpen, onClose, location }) => {
  const { t } = useTranslation();
  const [isMapLoading, setIsMapLoading] = useState(true);
  
  // Reset loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsMapLoading(true);
    }
  }, [isOpen]);
  
  if (!location.latitude || !location.longitude) return null;

  const mapSrc = `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&t=k&z=16&output=embed`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('tracking.modal.googleMaps.title', { plateNumber: location.plateNumber })}
      size="xl"
    >
      <div className="space-y-4">
        {/* Location Info */}
        <div className="bg-gray-50 p-3 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">{t('tracking.modal.googleMaps.truckLocation')}</h4>
              <p className="text-sm text-gray-600">
                📍 {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </p>
            </div>
            <button
              onClick={() => {
                const url = `https://www.google.com/maps?q=${location.latitude},${location.longitude}&t=satellite&z=16`;
                window.open(url, '_blank');
              }}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('tracking.modal.googleMaps.openInNewTab')}
            </button>
          </div>
        </div>

        {/* Embedded Map */}
        <div className="relative">
          {isMapLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">{t('common.loading')}</p>
              </div>
            </div>
          )}
          <iframe
            src={mapSrc}
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="rounded-lg"
            title={t('tracking.modal.googleMaps.mapTitle', { plateNumber: location.plateNumber })}
            onLoad={() => setIsMapLoading(false)}
          />
        </div>

        {/* Instructions */}
        <div className="text-xs text-gray-500 text-center">
          {t('tracking.modal.googleMaps.instructions')}
        </div>
      </div>
    </Modal>
  );
};

export default TruckTrackingPage;
